import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as React from "react"
import { CSSProperties } from "react"
import { connect } from "react-redux"
import type { TimeSpan } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/hours_of_operation_v2"

import { Alert, Col, Form, Row, Select } from "antd"
import { omit } from "ramda"
import { isEqual } from "lodash"
import {
    discountChanged,
    firstTimeDiscountChanged,
    moFirstOrderDiscountChanged,
    mibToggleLocationsModal,
    separateHooFormSubmitted,
    settingsImageUploadModalToggleClicked,
    terminalSettingFormSubmitted,
} from "../actions"
import DrawerBlockComponent from "../../../uiKit/Drawer/components/DrawerBlockComponent"
import {
    getDiscountSpecialPricingValue,
    getFirstTimeDiscountSpecialPricingValue,
    getMoFirstOrderDiscountPricing,
    getHooSettingsData,
    getMakeItButterSettings,
    isSettingSaving,
} from "../selectors"
import { LoadingOverlayDeprecated } from "../../../common/LoadingOverlayDeprecated"
import { MainMenuIconValue } from "./components/MibMainMenuIconSelect"
import type { SettingFormItem } from "../types"
import { getMibSelectedServiceData, getMibServicePickerSelectedId } from "../../../common/terminalPickers/selectors"
import type { HooSettingsResponse, HooSettingsSaveRequest, Terminal, Pricing } from "../../../../graphql/types"
import { TerminalType } from "../../../../graphql/types"
import { MibSettingsHeader } from "./components/MibSettingsHeader"
import { DeliverySettingsForm } from "./components/delivery/DeliverySettingsForm"
import { getDefaultPickupSettings } from "./utils"
import type { MiBOrderingModeType } from "./types"
import { MiBOrderingModeEnum } from "./types"
import { WrappedFormUtils } from "antd/lib/form/Form"
import { getTippingPercentageFormValues, getTippingPercentageKeys } from "../../../common/formItems/TipMethod/utils"
import { TipMethodFormComponent } from "../../../common/formItems/TipMethod/TipMethodFormComponent"
import { useMibSettingsHooks } from "./hooks"
import { MibOnlyGeneralSettings } from "./components/MibOnlyGeneralSettings"
import { PickupSettingsForm } from "./components/PickupSettingsForm"
import { OrderingModePickerSection } from "./components/OrderingModePickerSection"
import { DeliveryHooModal, PickupHooModal } from "./components/MibSettingsHooModals"
import { MibSettingsActionButtons } from "./components/MibSettingsActionButtons"
import { MibSeparatedGeneralSettings } from "./components/MibSeparatedGeneralSettings"
import { MobileGeneralSettings } from "./components/MobileGeneralSettings"
import { MO_DISCOUNT_FORM_FIELDS } from "./components/MoFirstOrderDiscount"
import { formItemLayout } from "../_components/SettingFormItems"
import { TipCalculationMethod } from "@figurepos/fgr-lib-business-logic/lib/receipts/types"

type Props = {
    moFirstOrderDiscountPricing: Maybe<Pricing>
    onMoFirstOrderDiscountChanged: typeof moFirstOrderDiscountChanged
    settings: SettingFormItem
    form: WrappedFormUtils
    isSaving: boolean
    isMobileOrderingOptionEnabled: boolean
    isLoading: boolean
    selectedTerminalId: Maybe<string>
    discountValue: Maybe<number>
    firstTimeDiscountValue: Maybe<number>
    selectedTerminalData: Maybe<Terminal>
    hooSettings: Maybe<HooSettingsResponse>
    saveMibSettings: ReturnType<typeof useMibSettingsHooks>["saveMibSettings"]
    mibSettings: ReturnType<typeof useMibSettingsHooks>["data"]
    saveSettingsClicked: (
        oldValues: SettingFormItem,
        newValues: Record<string, any>,
        terminalType: TerminalType
    ) => void
    onModalToggle: (opened: boolean, type: string) => void
    onDiscountChanged: (isOn: boolean, value: Maybe<number>, discountId?: Maybe<string>) => void
    onFirstTimeDiscountChanged: (isOn: boolean, value: Maybe<number>, discountId?: Maybe<string>) => void
    onChooseFileClicked: (opened: boolean, file: Maybe<string>) => void
    separateHooFormSubmitted: (data: HooSettingsSaveRequest) => void
}
type State = {
    applyDisabled: boolean
    mainMenuIcon: string
    isDiscountEnabled: boolean
    isFirstTimeDiscountEnabled: boolean
    isEcoFriendlyEnabled: boolean
    isDeliveryFutureOrders: boolean
    leadTimeActiveDelivery: boolean
    leadTimeActivePickup: boolean
    isPickupFutureOrders: boolean
    curbsideEnabled: boolean
    deliveryEnabled: boolean
    acceptOnlyFutureOrderForDelivery: boolean
    acceptOnlyFutureOrderForPickup: boolean
    pickupEnabled: boolean
    orderingMode: Maybe<MiBOrderingModeType>
    discountValue: Maybe<number>
    firstTimeDiscountValue: Maybe<number>
    futureDeliveryDays: Maybe<number>
    futurePickupDays: Maybe<number>
    tippingMethodError: Maybe<string>
    defaultDeliveryTime: Maybe<number>
    defaultPickupTime: Maybe<number>
    defaultLayoutTemplate: Maybe<number>
    isBannerMessageEnabled: boolean
    isAbilityToPayWithEgcEnabled: boolean
    pickupHoo: TimeSpan[]
    pickupHooModalOpen: boolean
    pickupHooActive: boolean
    deliveryHoo: TimeSpan[]
    deliveryHooModalOpen: boolean
    deliveryHooActive: boolean
    logoUrl: Maybe<string>
    desktopImageUrl: Maybe<string>
    iconUrl: Maybe<string>
    tipCalculationMethod: TipCalculationMethod
}

export const MIB_TIPPING_METHOD_PERCENTAGES = "MIB_TIPPING_METHOD_PERCENTAGES"
export const TIPPING_PRESELECTED_OPTION_INDEX = "TIPPING_PRESELECTED_OPTION_INDEX"
export const MIB_TIPPING_METHOD = "MIB_TIPPING_METHOD"

class MakeItButterSettingsForm extends React.Component<Props, State> {
    formItemLayout2 = {
        colon: false,
        labelCol: {
            xs: {
                span: 0,
            },
            sm: {
                span: 0,
            },
        },
        wrapperCol: {
            xs: {
                span: 24,
            },
            sm: {
                span: 24,
            },
        },
    }

    constructor(props: Props) {
        super(props)
        this.state = {
            applyDisabled: true,
            isDiscountEnabled: props.settings.MIB_PROMO_CODE_SP_ID.value
                ? !!props.settings.MIB_PROMO_CODE_SP_ID.value
                : false,
            isFirstTimeDiscountEnabled: props.settings.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.value
                ? !!props.settings.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.value
                : false,
            isEcoFriendlyEnabled: props.settings.MIB_ECO_FRIENDLY_OPTION_ENABLED.value
                ? !!props.settings.MIB_ECO_FRIENDLY_OPTION_ENABLED.value
                : false,
            deliveryEnabled: props.settings.MIB_DELIVERY_ENABLED.value
                ? !!props.settings.MIB_DELIVERY_ENABLED.value
                : false,
            mainMenuIcon: props.settings.MIB_MAIN_MENU_ICON.value || MainMenuIconValue.Menu,
            pickupEnabled: props.settings.MIB_PICKUP_ENABLED.value ? !!props.settings.MIB_PICKUP_ENABLED.value : false,
            orderingMode: props.settings.MIB_ORDERING_MODE.value,
            discountValue: props.discountValue,
            firstTimeDiscountValue: props.firstTimeDiscountValue,
            isDeliveryFutureOrders: props.settings.MIB_IS_FUTURE_ORDER_DELIVERY_VALUE.value
                ? !!props.settings.MIB_IS_FUTURE_ORDER_DELIVERY_VALUE.value
                : false,
            leadTimeActiveDelivery: props.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_DELIVERY.value
                ? !!props.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_DELIVERY.value
                : false,
            leadTimeActivePickup: props.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_PICKUP.value
                ? !!props.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_PICKUP.value
                : false,
            isPickupFutureOrders: props.settings.MIB_IS_FUTURE_ORDER_PICKUP_VALUE.value
                ? !!props.settings.MIB_IS_FUTURE_ORDER_PICKUP_VALUE.value
                : false,
            acceptOnlyFutureOrderForDelivery: props.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_DELIVERY.value
                ? !!props.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_DELIVERY.value
                : false,
            acceptOnlyFutureOrderForPickup: props.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_PICKUP.value
                ? !!props.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_PICKUP.value
                : false,
            futureDeliveryDays: props.settings.MIB_FUTURE_ORDER_DAYS_DELIVERY_VALUE.value,
            futurePickupDays: props.settings.MIB_FUTURE_ORDER_DAYS_PICKUP_VALUE.value,
            tippingMethodError: null,
            defaultDeliveryTime: props.settings.MIB_ESTIMATED_DELIVERY_TIME.value,
            defaultPickupTime: props.settings.MIB_ESTIMATED_PICKUP_TIME.value,
            defaultLayoutTemplate: props.settings.MIB_LAYOUT_TEMPLATE.value,
            isBannerMessageEnabled: props.settings.MIB_BANNER_MESSAGE.value
                ? !!props.settings.MIB_BANNER_MESSAGE.value
                : false,
            isAbilityToPayWithEgcEnabled: props.settings.MIB_ABILITY_TO_PAY_WITH_EGC.value
                ? !!props.settings.MIB_ABILITY_TO_PAY_WITH_EGC.value
                : false,
            curbsideEnabled: props.settings.MIB_PICKUP_CURBSIDE_OPTION_ENABLED.value
                ? !!props.settings.MIB_PICKUP_CURBSIDE_OPTION_ENABLED.value
                : false,
            pickupHooActive:
                props.hooSettings && props.hooSettings.pickupHoo ? props.hooSettings.pickupHoo.isActive : false,
            pickupHoo: props.hooSettings && props.hooSettings.pickupHoo ? props.hooSettings.pickupHoo.timeSpans : [],
            pickupHooModalOpen: false,
            deliveryHooActive:
                props.hooSettings && props.hooSettings.deliveryHoo ? props.hooSettings.deliveryHoo.isActive : false,
            deliveryHoo:
                props.hooSettings && props.hooSettings.deliveryHoo ? props.hooSettings.deliveryHoo.timeSpans : [],
            deliveryHooModalOpen: false,
            logoUrl: props.mibSettings?.logoUrl,
            desktopImageUrl: props.mibSettings?.desktopImageUrl,
            iconUrl: props.mibSettings?.iconUrl,
            tipCalculationMethod: props.settings.TIP_CALCULATION_METHOD.value,
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            nextProps.selectedTerminalId !== this.props.selectedTerminalId ||
            !isEqual(this.props.settings, nextProps.settings) ||
            !isEqual(this.props.mibSettings, nextProps.mibSettings)
        ) {
            const mibOrderingMode = nextProps.settings.MIB_ORDERING_MODE.value
            this.setState({
                deliveryEnabled: nextProps.settings.MIB_DELIVERY_ENABLED.value
                    ? !!nextProps.settings.MIB_DELIVERY_ENABLED.value
                    : false,
                mainMenuIcon: nextProps.settings.MIB_MAIN_MENU_ICON.value || MainMenuIconValue.Menu,
                isDiscountEnabled: nextProps.settings.MIB_PROMO_CODE_SP_ID.value
                    ? !!nextProps.settings.MIB_PROMO_CODE_SP_ID.value
                    : false,
                isFirstTimeDiscountEnabled: nextProps.settings.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.value
                    ? !!nextProps.settings.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.value
                    : false,
                isEcoFriendlyEnabled: nextProps.settings.MIB_ECO_FRIENDLY_OPTION_ENABLED.value
                    ? !!nextProps.settings.MIB_ECO_FRIENDLY_OPTION_ENABLED.value
                    : false,
                discountValue: nextProps.discountValue != null ? nextProps.discountValue : this.state.discountValue,
                firstTimeDiscountValue:
                    nextProps.firstTimeDiscountValue != null
                        ? nextProps.firstTimeDiscountValue
                        : this.state.firstTimeDiscountValue,
                isDeliveryFutureOrders: nextProps.settings.MIB_IS_FUTURE_ORDER_DELIVERY_VALUE.value
                    ? !!nextProps.settings.MIB_IS_FUTURE_ORDER_DELIVERY_VALUE.value
                    : false,
                leadTimeActiveDelivery: nextProps.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_DELIVERY.value
                    ? !!nextProps.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_DELIVERY.value
                    : false,
                leadTimeActivePickup: nextProps.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_PICKUP.value
                    ? !!nextProps.settings.MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_PICKUP.value
                    : false,
                isPickupFutureOrders: nextProps.settings.MIB_IS_FUTURE_ORDER_PICKUP_VALUE.value
                    ? !!nextProps.settings.MIB_IS_FUTURE_ORDER_PICKUP_VALUE.value
                    : false,
                pickupEnabled: nextProps.settings.MIB_PICKUP_ENABLED.value
                    ? !!nextProps.settings.MIB_PICKUP_ENABLED.value
                    : false,
                orderingMode: mibOrderingMode,
                acceptOnlyFutureOrderForDelivery: nextProps.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_DELIVERY.value
                    ? !!nextProps.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_DELIVERY.value
                    : false,
                acceptOnlyFutureOrderForPickup: nextProps.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_PICKUP.value
                    ? !!nextProps.settings.MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_PICKUP.value
                    : false,
                futureDeliveryDays: nextProps.settings.MIB_FUTURE_ORDER_DAYS_DELIVERY_VALUE.value,
                futurePickupDays: nextProps.settings.MIB_FUTURE_ORDER_DAYS_PICKUP_VALUE.value,
                tippingMethodError: null,
                defaultDeliveryTime: nextProps.settings.MIB_ESTIMATED_DELIVERY_TIME.value,
                defaultPickupTime: nextProps.settings.MIB_ESTIMATED_PICKUP_TIME.value,
                defaultLayoutTemplate: nextProps.settings.MIB_LAYOUT_TEMPLATE.value,
                isBannerMessageEnabled: nextProps.settings.MIB_BANNER_MESSAGE.value
                    ? !!nextProps.settings.MIB_BANNER_MESSAGE.value
                    : false,
                isAbilityToPayWithEgcEnabled: nextProps.settings.MIB_ABILITY_TO_PAY_WITH_EGC.value
                    ? !!nextProps.settings.MIB_ABILITY_TO_PAY_WITH_EGC.value
                    : false,
                curbsideEnabled: nextProps.settings.MIB_PICKUP_CURBSIDE_OPTION_ENABLED.value
                    ? !!nextProps.settings.MIB_PICKUP_CURBSIDE_OPTION_ENABLED.value
                    : false,
                logoUrl: nextProps.mibSettings?.logoUrl,
                desktopImageUrl: nextProps.mibSettings?.desktopImageUrl,
                iconUrl: nextProps.mibSettings?.iconUrl,
                tipCalculationMethod: nextProps.settings.TIP_CALCULATION_METHOD.value,
            })
            this.props.form.resetFields()
            this.props.form.setFieldsValue({
                MIB_ORDERING_MODE: mibOrderingMode,
            })
        }

        if (!isEqual(this.props.moFirstOrderDiscountPricing, nextProps.moFirstOrderDiscountPricing)) {
            this.props.form.resetFields(MO_DISCOUNT_FORM_FIELDS)
        }

        if (this.props.hooSettings !== nextProps.hooSettings) {
            this.setState({
                pickupHooActive:
                    nextProps.hooSettings && nextProps.hooSettings.pickupHoo
                        ? nextProps.hooSettings.pickupHoo.isActive
                        : false,
                pickupHoo:
                    nextProps.hooSettings && nextProps.hooSettings.pickupHoo
                        ? nextProps.hooSettings.pickupHoo.timeSpans
                        : [],
                pickupHooModalOpen: false,
                deliveryHooActive:
                    nextProps.hooSettings && nextProps.hooSettings.deliveryHoo
                        ? nextProps.hooSettings.deliveryHoo.isActive
                        : false,
                deliveryHoo:
                    nextProps.hooSettings && nextProps.hooSettings.deliveryHoo
                        ? nextProps.hooSettings.deliveryHoo.timeSpans
                        : [],
                deliveryHooModalOpen: false,
            })
        }
    }

    isMobileOrderingEnabled = () =>
        !!(
            this.props.isMobileOrderingOptionEnabled &&
            this.props.selectedTerminalData?.metadata?.onlineOrdering?.mobileOrderingEnabled
        )

    isWebOrderingEnabled = () => this.props.selectedTerminalData?.metadata?.onlineOrdering?.webOrderingEnabled !== false

    noTerminalsSelected = () => !this.props.selectedTerminalId

    onStateValueChanged = (stateKey: string, value: Maybe<any>) => {
        // @ts-expect-error
        this.setState({
            [stateKey]: value,
        })
        this.onChangeCallback()
    }

    onChangeCallback = () => {
        this.setState({
            applyDisabled: false,
        })
    }

    onOrderingModeChange = (type: Maybe<string>) => {
        if (type === MiBOrderingModeEnum.DineIn) {
            this.setState({
                deliveryEnabled: false,
                pickupEnabled: true,
            })
        }

        this.onChangeCallback()
    }

    getTippingSectionTitle = () =>
        this.isWebOrderingEnabled() && this.isMobileOrderingEnabled() ? "Tipping (MIB + Mobile)" : "Tipping"

    handleSubmit = () => {
        this.props.form.validateFields(async (err, values) => {
            const tippingPercentages: number[] = getTippingPercentageFormValues(values, MIB_TIPPING_METHOD_PERCENTAGES)
            const valuesWithoutTipPercentages = omit(
                [...getTippingPercentageKeys(MIB_TIPPING_METHOD_PERCENTAGES), ...MO_DISCOUNT_FORM_FIELDS],
                values
            )

            // Validate Tipping Method Percentages
            if (tippingPercentages.length < 1) {
                this.setState({
                    tippingMethodError: "Please enter at least one Tip Guide Percentage",
                })
                return
            }

            const anyFieldErrors = Object.values(this.props.form.getFieldsError()).some(v => v)

            if (!err && !anyFieldErrors) {
                this.props.saveSettingsClicked(
                    this.props.settings,
                    {
                        ...valuesWithoutTipPercentages,
                        MIB_MAIN_MENU_ICON: this.state.mainMenuIcon,
                        MIB_BANNER_MESSAGE: this.state.isBannerMessageEnabled ? values.MIB_BANNER_MESSAGE : null,
                        MIB_PRIMARY_COLOR: values.MIB_PRIMARY_COLOR ? `#${values.MIB_PRIMARY_COLOR}` : null,
                        MIB_DELIVERY_ENABLED: this.state.deliveryEnabled,
                        MIB_ECO_FRIENDLY_OPTION_ENABLED: this.state.isEcoFriendlyEnabled,
                        MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_DELIVERY: this.state.acceptOnlyFutureOrderForDelivery,
                        MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_PICKUP: this.state.acceptOnlyFutureOrderForPickup,
                        MIB_PICKUP_ENABLED: this.state.pickupEnabled,
                        MIB_IS_FUTURE_ORDER_DELIVERY_VALUE: this.state.isDeliveryFutureOrders,
                        MIB_FUTURE_ORDER_DAYS_DELIVERY_VALUE: this.state.futureDeliveryDays,
                        MIB_IS_FUTURE_ORDER_PICKUP_VALUE: this.state.isPickupFutureOrders,
                        MIB_FUTURE_ORDER_DAYS_PICKUP_VALUE: this.state.futurePickupDays,
                        MIB_MIN_DELIVERY_VALUE:
                            values.MIB_MIN_DELIVERY_VALUE === undefined
                                ? undefined
                                : values.MIB_MIN_DELIVERY_VALUE
                                  ? values.MIB_MIN_DELIVERY_VALUE * 100
                                  : null,
                        MIB_MIN_PICKUP_VALUE:
                            values.MIB_MIN_PICKUP_VALUE === undefined
                                ? undefined
                                : values.MIB_MIN_PICKUP_VALUE
                                  ? values.MIB_MIN_PICKUP_VALUE * 100
                                  : null,
                        MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_DELIVERY: this.state.leadTimeActiveDelivery,
                        MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_PICKUP: this.state.leadTimeActivePickup,
                        [MIB_TIPPING_METHOD_PERCENTAGES]: tippingPercentages,
                        MIB_ESTIMATED_DELIVERY_TIME: this.state.defaultDeliveryTime,
                        MIB_ESTIMATED_PICKUP_TIME: this.state.defaultPickupTime,
                        MIB_LAYOUT_TEMPLATE: this.state.defaultLayoutTemplate,
                        MIB_ABILITY_TO_PAY_WITH_EGC: this.state.isAbilityToPayWithEgcEnabled,
                        MIB_PICKUP_CURBSIDE_OPTION_ENABLED: this.state.curbsideEnabled,
                        TIP_CALCULATION_METHOD: this.state.tipCalculationMethod,
                        ...(this.state.orderingMode === MiBOrderingModeEnum.DineIn ? getDefaultPickupSettings() : {}),
                    },
                    TerminalType.Mib
                )
                this.props.separateHooFormSubmitted({
                    deliveryHoo: {
                        isActive: this.state.deliveryHooActive,
                        timeSpans: this.state.deliveryHoo,
                    },
                    pickupHoo: {
                        isActive: this.state.pickupHooActive,
                        timeSpans: this.state.pickupHoo,
                    },
                })
                this.props.onDiscountChanged(
                    this.state.isDiscountEnabled,
                    this.state.discountValue || this.props.discountValue
                )
                this.props.onFirstTimeDiscountChanged(
                    this.state.isFirstTimeDiscountEnabled,
                    this.state.firstTimeDiscountValue || this.props.firstTimeDiscountValue
                )
                if (this.isMobileOrderingEnabled()) {
                    this.props.onMoFirstOrderDiscountChanged(
                        values.moDiscountEnabled,
                        values.moDiscountType,
                        values.moDiscountValue,
                        values.moDiscountName
                    )
                }

                await this.props.saveMibSettings({
                    logoUrl: this.state.logoUrl,
                    bgImageUrl: this.state.desktopImageUrl,
                    iconUrl: this.state.iconUrl,
                })

                this.setState({
                    applyDisabled: true,
                })
            }
        })
    }

    renderSettingsBasedOnOrderingMode = () => {
        const orderingMode =
            this.props.form.getFieldValue("MIB_ORDERING_MODE") ||
            this.props.settings.MIB_ORDERING_MODE.value ||
            MiBOrderingModeEnum.Delivery
        return (
            <>
                {orderingMode === MiBOrderingModeEnum.Delivery ? (
                    <DeliverySettingsForm
                        form={this.props.form}
                        settings={this.props.settings}
                        deliveryEnabled={this.state.deliveryEnabled}
                        defaultDeliveryTime={this.state.defaultDeliveryTime}
                        onChangeCallback={this.onChangeCallback}
                        onStateValueChanged={this.onStateValueChanged}
                        acceptOnlyFutureOrderForDelivery={this.state.acceptOnlyFutureOrderForDelivery}
                        futureDeliveryDays={this.state.futureDeliveryDays}
                        isDeliveryFutureOrders={this.state.isDeliveryFutureOrders}
                        leadTimeActiveDelivery={this.state.leadTimeActiveDelivery}
                        deliveryHooActive={this.state.deliveryHooActive}
                        deliveryHoo={this.state.deliveryHoo}
                        isWebAndMobileOrderingEnabled={this.isWebOrderingEnabled() && this.isMobileOrderingEnabled()}
                        onDeliveryHooModalOpen={() =>
                            this.setState({
                                deliveryHooModalOpen: true,
                            })
                        }
                        onDeliveryHooActiveToggle={(active: boolean) =>
                            this.setState({
                                deliveryHooActive: active,
                                applyDisabled: false,
                            })
                        }
                    />
                ) : null}

                {orderingMode === MiBOrderingModeEnum.Delivery || orderingMode === MiBOrderingModeEnum.DineIn ? (
                    <PickupSettingsForm
                        dineInEnabled={orderingMode === MiBOrderingModeEnum.DineIn}
                        pickupEnabled={this.state.pickupEnabled}
                        pickupHoo={this.state.pickupHoo}
                        pickupHooModalOpen={this.state.pickupHooModalOpen}
                        pickupHooActive={this.state.pickupHooActive}
                        isPickupFutureOrders={this.state.isPickupFutureOrders}
                        curbsideEnabled={this.state.curbsideEnabled}
                        acceptOnlyFutureOrderForPickup={this.state.acceptOnlyFutureOrderForPickup}
                        futurePickupDays={this.state.futurePickupDays}
                        defaultPickupTime={this.state.defaultPickupTime}
                        leadTimeActivePickup={this.state.leadTimeActivePickup}
                        settings={this.props.settings}
                        form={this.props.form}
                        isWebAndMobileOrderingEnabled={this.isWebOrderingEnabled() && this.isMobileOrderingEnabled()}
                        onStateValueChanged={this.onStateValueChanged}
                        onChangeCallback={this.onChangeCallback}
                    />
                ) : null}
            </>
        )
    }

    renderContent = () => {
        if (this.noTerminalsSelected()) {
            return null
        }

        if (!this.isMobileOrderingEnabled() && !this.isWebOrderingEnabled()) {
            return (
                <Alert
                    className={classes.disabledAlert}
                    message="Make It Butter and Mobile Ordering are disabled for this Service"
                    type="warning"
                />
            )
        }

        return (
            <div className={classes.wrap}>
                {this.props.selectedTerminalData && (
                    <MibSettingsHeader
                        selectedTerminalData={this.props.selectedTerminalData}
                        isMobileOrderingEnabled={this.isMobileOrderingEnabled()}
                        isWebOrderingEnabled={this.isWebOrderingEnabled()}
                    />
                )}

                <Row className={classes.contentWrap}>
                    <Col span={24}>
                        <div>
                            <Form className="settings-form-wrapper receipt" onSubmit={this.handleSubmit}>
                                {/* General settings - MIB Enabled, Mobile Enabled */}
                                {this.isWebOrderingEnabled() && this.isMobileOrderingEnabled() ? (
                                    <>
                                        <MobileGeneralSettings
                                            moFirstOrderDiscountPricing={this.props.moFirstOrderDiscountPricing}
                                            isDiscountEnabled={this.state.isDiscountEnabled}
                                            isFirstTimeDiscountEnabled={this.state.isFirstTimeDiscountEnabled}
                                            discountValue={this.state.discountValue}
                                            firstTimeDiscountValue={this.state.firstTimeDiscountValue}
                                            isAbilityToPayWithEgcEnabled={this.state.isAbilityToPayWithEgcEnabled}
                                            settings={this.props.settings}
                                            form={this.props.form}
                                            onChangeCallback={this.onChangeCallback}
                                            onStateValueChanged={this.onStateValueChanged}
                                        />
                                        <MibSeparatedGeneralSettings
                                            mainMenuIcon={this.state.mainMenuIcon}
                                            defaultLayoutTemplate={this.state.defaultLayoutTemplate}
                                            isDiscountEnabled={this.state.isDiscountEnabled}
                                            isFirstTimeDiscountEnabled={this.state.isFirstTimeDiscountEnabled}
                                            isEcoFriendlyEnabled={this.state.isEcoFriendlyEnabled}
                                            discountValue={this.state.discountValue}
                                            firstTimeDiscountValue={this.state.firstTimeDiscountValue}
                                            isBannerMessageEnabled={this.state.isBannerMessageEnabled}
                                            isAbilityToPayWithEgcEnabled={this.state.isAbilityToPayWithEgcEnabled}
                                            logoUrl={this.state.logoUrl}
                                            desktopImageUrl={this.state.desktopImageUrl}
                                            iconUrl={this.state.iconUrl}
                                            settings={this.props.settings}
                                            form={this.props.form}
                                            selectedTerminalData={this.props.selectedTerminalData}
                                            onChangeCallback={this.onChangeCallback}
                                            onStateValueChanged={this.onStateValueChanged}
                                            onOrderingModeChange={this.onOrderingModeChange}
                                        />
                                    </>
                                ) : null}
                                {/* General settings - MIB Enabled, Mobile Disabled */}
                                {this.isWebOrderingEnabled() && !this.isMobileOrderingEnabled() ? (
                                    <>
                                        <MibOnlyGeneralSettings
                                            mainMenuIcon={this.state.mainMenuIcon}
                                            defaultLayoutTemplate={this.state.defaultLayoutTemplate}
                                            isDiscountEnabled={this.state.isDiscountEnabled}
                                            isFirstTimeDiscountEnabled={this.state.isFirstTimeDiscountEnabled}
                                            isEcoFriendlyEnabled={this.state.isEcoFriendlyEnabled}
                                            discountValue={this.state.discountValue}
                                            firstTimeDiscountValue={this.state.firstTimeDiscountValue}
                                            isBannerMessageEnabled={this.state.isBannerMessageEnabled}
                                            isAbilityToPayWithEgcEnabled={this.state.isAbilityToPayWithEgcEnabled}
                                            logoUrl={this.state.logoUrl}
                                            desktopImageUrl={this.state.desktopImageUrl}
                                            iconUrl={this.state.iconUrl}
                                            settings={this.props.settings}
                                            form={this.props.form}
                                            onChangeCallback={this.onChangeCallback}
                                            onStateValueChanged={this.onStateValueChanged}
                                        />
                                        <DrawerBlockComponent title="Select Ordering Scheme">
                                            <OrderingModePickerSection
                                                settings={this.props.settings}
                                                form={this.props.form}
                                                onOrderingModeChange={this.onOrderingModeChange}
                                            />
                                        </DrawerBlockComponent>
                                    </>
                                ) : null}
                                {/* General settings - MIB Disabled, Mobile Enabled */}
                                {!this.isWebOrderingEnabled() && this.isMobileOrderingEnabled() ? (
                                    <MobileGeneralSettings
                                        moFirstOrderDiscountPricing={this.props.moFirstOrderDiscountPricing}
                                        isDiscountEnabled={this.state.isDiscountEnabled}
                                        isFirstTimeDiscountEnabled={this.state.isFirstTimeDiscountEnabled}
                                        discountValue={this.state.discountValue}
                                        firstTimeDiscountValue={this.state.firstTimeDiscountValue}
                                        isAbilityToPayWithEgcEnabled={this.state.isAbilityToPayWithEgcEnabled}
                                        settings={this.props.settings}
                                        form={this.props.form}
                                        onChangeCallback={this.onChangeCallback}
                                        onStateValueChanged={this.onStateValueChanged}
                                    />
                                ) : null}
                                {this.renderSettingsBasedOnOrderingMode()}
                                <DrawerBlockComponent
                                    title={this.getTippingSectionTitle()}
                                    style={{ padding: "1.25rem" }}>
                                    <Form.Item
                                        {...formItemLayout}
                                        style={styles.noBottomMargin}
                                        label="Tip Calculation Method">
                                        <Select
                                            defaultValue={this.state.tipCalculationMethod}
                                            value={
                                                this.state.tipCalculationMethod === undefined
                                                    ? "Multiple Values"
                                                    : this.state.tipCalculationMethod
                                            }
                                            onChange={value => {
                                                this.setState({
                                                    // @ts-expect-error
                                                    tipCalculationMethod: value || null,
                                                })
                                                this.onChangeCallback()
                                            }}
                                            style={{
                                                width: "14rem",
                                            }}>
                                            <Select.Option value={TipCalculationMethod.Subtotal}>
                                                Subtotal
                                            </Select.Option>
                                            <Select.Option value={TipCalculationMethod.SubtotalWithTaxes}>
                                                Subtotal + Taxes
                                            </Select.Option>
                                            <Select.Option value={TipCalculationMethod.OrderTotal}>
                                                Order Total
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>
                                    <div style={styles.tipCalculationMethodDescription}>
                                        <span style={styles.description}>
                                            <strong>Subtotal (Recommended):</strong> Tip is calculated{" "}
                                            <span style={{ textDecoration: "underline" }}>
                                                before taxes and order-level special pricing
                                            </span>
                                        </span>
                                        <span style={styles.description}>
                                            <strong>Subtotal + Taxes:</strong> Tip is calculated after taxes but{" "}
                                            <span style={{ textDecoration: "underline" }}>
                                                before order-level special pricing
                                            </span>
                                        </span>
                                        <span style={styles.description}>
                                            <strong>Order Total:</strong> Tip is calculated after taxes and order-level
                                            special pricing
                                        </span>
                                    </div>
                                    <TipMethodFormComponent
                                        hideDisabledOption={true}
                                        settings={this.props.settings}
                                        formKeys={{
                                            tipMethod: MIB_TIPPING_METHOD,
                                            tipMethodPercentages: MIB_TIPPING_METHOD_PERCENTAGES,
                                            preselectedTip: TIPPING_PRESELECTED_OPTION_INDEX,
                                        }}
                                        form={this.props.form}
                                        error={this.state.tippingMethodError}
                                        onChangeCallback={this.onChangeCallback}
                                    />
                                </DrawerBlockComponent>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </div>
        )
    }

    render() {
        return (
            <div className="mib-settings-wrapper">
                <style jsx={true} global={true}>
                    {`
                        .mib-settings-wrapper {
                            padding-top: 7.65rem;
                        }

                        .mib-settings-wrapper .ant-input-number-input {
                            text-align: right;
                        }

                        .mib-settings-wrapper .ant-form-item-required:before {
                            display: none;
                        }

                        .promo-code-word .ant-form-explain {
                            position: absolute;
                            right: 0px;
                            padding-top: 4px;
                        }

                        .lead-time-input input {
                            text-align: right;
                        }

                        .lead-time-hours-input input {
                            padding-right: 44px !important;
                        }

                        .lead-time-days-input input {
                            padding-right: 40px !important;
                        }

                        .mib-settings-wrapper .ant-form-item-label {
                            overflow: visible;
                        }

                        .color-picker-input .ant-input-suffix {
                            right: 0;
                        }

                        .lead-time-input .ant-input-suffix {
                            right: 4px;
                        }
                    `}
                </style>
                <DeliveryHooModal
                    deliveryHoo={this.state.deliveryHoo}
                    deliveryHooModalOpen={this.state.deliveryHooModalOpen}
                    deliveryHooActive={this.state.deliveryHooActive}
                    onDoneClicked={() => {
                        this.setState({
                            deliveryHooModalOpen: false,
                        })
                    }}
                    onChange={value => {
                        this.setState({
                            deliveryHoo: value,
                            applyDisabled: false,
                        })
                    }}
                />
                <PickupHooModal
                    pickupHoo={this.state.pickupHoo}
                    pickupHooModalOpen={this.state.pickupHooModalOpen}
                    pickupHooActive={this.state.pickupHooActive}
                    onDoneClicked={() => {
                        this.setState({
                            pickupHooModalOpen: false,
                        })
                    }}
                    onChange={value => {
                        this.setState({
                            pickupHoo: value,
                            applyDisabled: false,
                        })
                    }}
                />
                <MibSettingsActionButtons
                    isSaving={this.props.isSaving}
                    applyDisabled={this.state.applyDisabled}
                    onSaveClicked={this.handleSubmit}
                />
                {this.noTerminalsSelected() && (
                    <div style={styles.noTerminalsWrap}>
                        <Alert
                            description="Please select at least one Online Ordering service from the picker above"
                            message="No services selected"
                            type="info"
                        />
                    </div>
                )}
                {this.props.isLoading ? (
                    <LoadingOverlayDeprecated
                        style={{
                            position: "relative",
                        }}
                    />
                ) : (
                    this.renderContent()
                )}
            </div>
        )
    }
}

const styles: Record<string, CSSProperties> = {
    noTerminalsWrap: {
        marginTop: "1.5rem",
        maxWidth: "25rem",
        paddingBottom: "2rem",
        paddingLeft: "1rem",
    },
    description: {
        fontSize: "12px",
        color: "#7E7E7E",
        display: "block",
    },
    tipCalculationMethodDescription: {
        lineHeight: "1.3",
        paddingBottom: "1rem",
        marginBottom: "1rem",
        borderBottom: "1px solid #E1E6EB",
    },
    noBottomMargin: {
        marginBottom: 0,
    },
}

const classes = {
    wrap: "w-full max-w-[55rem]",
    contentWrap: "p-4",
    disabledAlert: "ml-4 mt-6 w-full max-w-2xl",
}

const mapStateToProps = state => ({
    settings: getMakeItButterSettings(state),
    isSaving: isSettingSaving(state),
    discountValue: getDiscountSpecialPricingValue(state),
    firstTimeDiscountValue: getFirstTimeDiscountSpecialPricingValue(state),
    moFirstOrderDiscountPricing: getMoFirstOrderDiscountPricing(state),
    selectedTerminalData: getMibSelectedServiceData(state),
    selectedTerminalId: getMibServicePickerSelectedId(state),
    hooSettings: getHooSettingsData(state),
})

const mapDispatchToProps = {
    onChooseFileClicked: settingsImageUploadModalToggleClicked,
    onModalToggle: mibToggleLocationsModal,
    onDiscountChanged: discountChanged,
    onFirstTimeDiscountChanged: firstTimeDiscountChanged,
    onMoFirstOrderDiscountChanged: moFirstOrderDiscountChanged,
    saveSettingsClicked: terminalSettingFormSubmitted,
    separateHooFormSubmitted: separateHooFormSubmitted,
}
export default connect<any, any, any>(mapStateToProps, mapDispatchToProps)(Form.create()(MakeItButterSettingsForm))
